import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  isActionIntent,
  parseActions,
  stripJsonBlocks,
  extractUnknownActions,
  cleanAssistantDisplayText,
} from './ai/aiUtils';
import { buildSlimPrompt, buildFullPrompt } from './ai/aiPrompts';
import { makeExecuteAction } from './ai/aiActions';
import { Message, ActionBadge, StatsBar } from './ai/AIChatUI';
import SecureCredentialPopup from './SecureCredentialPopup';

const { FiSend, FiZap, FiLoader, FiTrash2, FiChevronDown, FiKey, FiMic, FiMicOff } = FiIcons;

const MODEL = import.meta.env.VITE_OPENROUTER_MODEL || 'openrouter/free';

// Resilient Free Model Fallback Pool for OpenRouter
const FREE_MODELS_POOL = [
  MODEL,
  'openrouter/free',
  'google/gemma-4-26b-a4b-it:free',
  'nvidia/nemotron-3.5-lightning:free',
  'meta-llama/llama-3.3-70b-instruct:free',
  'cohere/north-mini-code:free',
  'liquid/lfm-2.5-2.6b:free',
].filter((m, idx, arr) => m && arr.indexOf(m) === idx);

const WELCOME_MSG = {
  role: 'assistant',
  id: 'init',
  content:
    `Hey! I'm your bookmark assistant ✨\n\n` +
    `I can help you organize folders, manage bookmarks, change themes, or find specific links.\n\n` +
    `Just ask me what you need!`,
};

const NAMED_HEX = {
  white: '#FFFFFF',
  black: '#000000',
  red: '#FF0000',
  green: '#008000',
  blue: '#0000FF',
  yellow: '#FFFF00',
  gray: '#808080',
  grey: '#808080',
  orange: '#FFA500',
  purple: '#800080',
  pink: '#FFC0CB',
  brown: '#A52A2A',
  cyan: '#00FFFF',
  magenta: '#FF00FF',
};

const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

const DESTRUCTIVE_ACTIONS = new Set([
  'delete_bookmark',
  'delete_folder',
  'bulk_delete',
  'revoke_shared_member',
  'leave_shared_folder',
]);

const CONFIRM_WORDS = new Set(['confirm', 'yes', 'y', 'proceed']);
const CANCEL_WORDS = new Set(['cancel', 'no', 'n', 'stop', 'abort']);

function toHexIfNamed(value) {
  if (!value || typeof value !== 'string') return null;
  const v = value.trim().toLowerCase();
  if (HEX_RE.test(v)) return v.toUpperCase();
  if (NAMED_HEX[v]) return NAMED_HEX[v];
  return null;
}

function normalizeCommand(text = '') {
  return text.trim().toLowerCase();
}

function isConfirmCommand(text = '') {
  return CONFIRM_WORDS.has(normalizeCommand(text));
}

function isCancelCommand(text = '') {
  return CANCEL_WORDS.has(normalizeCommand(text));
}

function isDestructiveAction(action) {
  return !!action?.action && DESTRUCTIVE_ACTIONS.has(action.action);
}

function summarizeActionForConfirm(action) {
  switch (action.action) {
    case 'delete_bookmark':
      return `- Delete bookmark "${action.title || action.name || action.id || action.bookmarkId || 'Unknown'}"`;
    case 'delete_folder':
      return `- Delete folder "${action.title || action.folderTitle || action.name || action.id || action.folderId || 'Unknown'}"`;
    case 'bulk_delete':
      return `- Bulk delete ${action.ids?.length || 0} item(s)`;
    case 'revoke_shared_member':
      return `- Remove shared member "${action.inviteeEmail || action.email || 'Unknown'}"`;
    case 'leave_shared_folder':
      return `- Leave shared folder "${action.folderTitle || action.folderId || 'Unknown'}"`;
    default:
      return `- ${action.action}`;
  }
}

function buildConfirmationMessage(actions = []) {
  return [
    `I’m about to perform these destructive actions:`,
    ...actions.map(summarizeActionForConfirm),
    '',
    `Reply **confirm** to continue or **cancel** to abort.`,
  ].join('\n');
}

function actionToHistoryText(action) {
  switch (action.action) {
    case 'add_folder':
      return `[DONE] Created folder "${action.title}" under parent "${action.parentId || 'root'}"`;
    case 'add_bookmark':
      return `[DONE] Added bookmark "${action.title}" (${action.url}) to folder "${action.folderTitle || action.folderId || 'root'}"${action.credentials ? ' with credentials' : ''}`;
    case 'delete_bookmark':
      return `[DONE] Deleted bookmark id:${action.id}`;
    case 'delete_folder':
      return `[DONE] Deleted folder id:${action.id || action.title}`;
    case 'rename_bookmark':
      return `[DONE] Renamed bookmark id:${action.id} to "${action.title || action.newTitle || action.toTitle || 'Untitled'}"`;
    case 'rename_folder':
      return `[DONE] Renamed folder id:${action.id || action.folderId || 'unknown'} to "${action.title || action.newTitle || action.toTitle || 'Untitled'}"`;
    case 'move_bookmark':
      return `[DONE] Moved bookmark id:${action.id} to folder "${action.targetFolderTitle || action.targetFolderId}"`;
    case 'move_folder':
      return `[DONE] Moved folder "${action.folderTitle || action.folderId}" to "${action.targetParentTitle || action.targetParentId}"`;
    case 'update_note':
      return `[DONE] Updated note for bookmark id:${action.id}`;
    case 'update_credentials':
      return `[DONE] Updated credentials for bookmark id:${action.id}`;
    case 'get_credentials':
      return action.result ? `[DONE] ${action.result}` : `[DONE] Retrieved credentials for ${action.title || action.id || 'bookmark'}`;
    case 'get_note':
      return action.result ? `[DONE] ${action.result}` : `[DONE] Retrieved note for ${action.title || action.id || 'bookmark'}`;
    case 'update_url':
      return `[DONE] Updated URL for bookmark id:${action.id}`;
    case 'update_bookmark':
      return `[DONE] Updated bookmark details for id:${action.id}`;
    case 'list_bookmarks':
      return action.result
        ? `[DONE] Listed bookmarks${action.folderTitle ? ` in folder "${action.folderTitle}"` : ''}`
        : `[DONE] Listed bookmarks`;
    case 'share_folder':
      return `[DONE] Shared folder "${action.folderTitle}" with ${action.inviteeEmail} as ${action.permission}`;
    case 'revoke_shared_member':
      return `[DONE] Revoked access for ${action.inviteeEmail} from folder "${action.folderTitle}"`;
    case 'update_permission':
      return `[DONE] Updated ${action.inviteeEmail} to ${action.permission} in folder "${action.folderTitle}"`;
    case 'bulk_delete':
      return `[DONE] Bulk deleted ${action.ids?.length || 0} items`;
    case 'apply_theme':
      return `[DONE] Updated accent to "${action.customAccent || action.accent}"`;
    case 'apply_background':
      return `[DONE] Updated background (${action.backgroundType || action.customUrl || action.pageBg || 'color'})`;
    case 'apply_mode':
      return `[DONE] Switched to ${action.mode} mode`;
    case 'apply_font_colors':
      return `[DONE] Updated color tokens`;
    case 'reset_font_colors':
      return `[DONE] Reset color tokens`;
    default:
      return `[DONE] ${action.action}`;
  }
}

function stableStringify(obj) {
  try {
    return JSON.stringify(obj, Object.keys(obj).sort());
  } catch {
    return JSON.stringify(obj);
  }
}

function dedupeActions(actions = []) {
  const seen = new Set();
  const out = [];
  for (const a of actions) {
    const key = `${a.action}::${stableStringify(a)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(a);
  }
  return out;
}


function normalizeAction(a) {
  if (!a || !a.action) return a;
  const actionMap = {
    set_animation: 'apply_background',
    change_animation: 'apply_background',
    set_background: 'apply_background',
    change_background: 'apply_background',
    set_colors: 'apply_font_colors',
    change_colors: 'apply_font_colors',
  };

  const next = { ...a, action: actionMap[a.action] || a.action };

  if (!next.backgroundType && next.animation) next.backgroundType = next.animation;
  if (!next.pageBg && next.background) next.pageBg = next.background;

  return next;
}

function lastUserText(messages = []) {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]?.role === 'user' && messages[i]?.content) return messages[i].content;
  }
  return '';
}

const KNOWN_SITES = {
  jitsi: 'https://meet.jit.si',
  meet: 'https://meet.jit.si',
  youtube: 'https://youtube.com',
  yt: 'https://youtube.com',
  google: 'https://google.com',
  github: 'https://github.com',
  gh: 'https://github.com',
  twitter: 'https://x.com',
  x: 'https://x.com',
  reddit: 'https://reddit.com',
  linkedin: 'https://linkedin.com',
  facebook: 'https://facebook.com',
  fb: 'https://facebook.com',
  instagram: 'https://instagram.com',
  ig: 'https://instagram.com',
  netflix: 'https://netflix.com',
  spotify: 'https://spotify.com',
  amazon: 'https://amazon.com',
  chatgpt: 'https://chatgpt.com',
  claude: 'https://claude.ai',
  gmail: 'https://mail.google.com',
  twitch: 'https://twitch.tv',
  chess: 'https://chess.com',
  lichess: 'https://lichess.org',
  discord: 'https://discord.com',
  whatsapp: 'https://web.whatsapp.com',
  telegram: 'https://web.telegram.org',
  notion: 'https://notion.so',
  figma: 'https://figma.com',
  stackoverflow: 'https://stackoverflow.com',
  wikipedia: 'https://wikipedia.org',
  zoom: 'https://zoom.us',
  slack: 'https://slack.com',
};

function normalizeShortcutUrl(value = '') {
  let s = (value || '').trim();
  if (!s) return null;

  s = s.replace(/^(site|website|link|url)\s+/i, '').trim();
  if (!s) return null;

  if (/^https?:\/\//i.test(s)) return s;
  if (/^www\./i.test(s)) return `https://${s}`;

  const lower = s.toLowerCase();
  if (KNOWN_SITES[lower]) return KNOWN_SITES[lower];

  if (/^[a-z0-9-]+(\.[a-z0-9-]+)+([/:?#].*)?$/i.test(s)) {
    return `https://${s}`;
  }

  if (/^[a-z0-9-]+$/i.test(s)) {
    return `https://${s}.com`;
  }

  return null;
}

function titleFromUrl(url = '') {
  try {
    const clean = url
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .split('/')[0]
      .split('?')[0]
      .split('#')[0];

    const base = clean.split('.')[0] || clean;
    if (!base) return 'New Bookmark';

    return base.charAt(0).toUpperCase() + base.slice(1);
  } catch {
    return 'New Bookmark';
  }
}

function maybeParseBookmarkShortcut(text = '') {
  const raw = text.trim();
  if (!raw) return null;

  const match = raw.match(/^(bm|bmk|bookmark|save)\s+(.+)$/i);
  if (!match) return null;

  const targetRaw = (match[2] || '').trim();
  if (!targetRaw) return null;

  const url = normalizeShortcutUrl(targetRaw);
  if (!url) return null;

  return [
    {
      action: 'add_bookmark',
      url,
      title: titleFromUrl(url),
      folderId: 'root',
      folderTitle: 'Home',
    },
  ];
}

function maybeParseDirectOpen(text = '', data = {}) {
  const raw = (text || '').trim();
  if (!raw) return null;

  const match = raw.match(/^(?:open|launch|visit|go\s+to|navigate\s+to)\s+(.+)$/i);
  if (!match) return null;

  let target = (match[1] || '').trim();
  target = target.replace(/^["'`]|["'`]$/g, '').replace(/[.?!]+$/, '').trim();
  if (!target) return null;

  const targetLower = target.toLowerCase();
  const genericWords = new Set(['bookmark', 'bookmarks', 'folder', 'folders', 'tab', 'tabs', 'all', 'everything', 'link', 'links', 'website', 'settings', 'modal']);
  if (genericWords.has(targetLower)) return null;

  const allBookmarks = Object.values(data || {}).flatMap((f) => (f.items || []).filter((i) => i.type === 'link'));

  // 1. Exact title match
  const exactBm = allBookmarks.find((b) => (b.title || '').trim().toLowerCase() === targetLower);
  if (exactBm) {
    return {
      action: 'open_bookmark',
      id: exactBm.id,
      title: exactBm.title,
      url: exactBm.url,
    };
  }

  // 2. Exact URL or domain match
  const targetCleanDomain = targetLower.replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/+$/, '');
  const exactUrlBm = allBookmarks.find((b) => {
    if (!b.url) return false;
    const bClean = b.url.toLowerCase().replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/+$/, '');
    return bClean === targetCleanDomain || bClean.startsWith(targetCleanDomain + '/');
  });
  if (exactUrlBm) {
    return {
      action: 'open_bookmark',
      id: exactUrlBm.id,
      title: exactUrlBm.title,
      url: exactUrlBm.url,
    };
  }

  // 3. Partial title match (e.g. "chess" matching "Chess.com")
  const partialBm = allBookmarks.find((b) => {
    const t = (b.title || '').toLowerCase();
    return t.split(/[\s-]+/).some((word) => word === targetLower) || t === targetLower;
  });
  if (partialBm) {
    return {
      action: 'open_bookmark',
      id: partialBm.id,
      title: partialBm.title,
      url: partialBm.url,
    };
  }

  // 4. Resolve external known site or standard web domain
  const resolvedUrl = normalizeShortcutUrl(target);
  if (resolvedUrl) {
    const displayTitle = KNOWN_SITES[targetLower]
      ? target.charAt(0).toUpperCase() + target.slice(1)
      : titleFromUrl(resolvedUrl);

    return {
      action: 'open_bookmark',
      title: displayTitle,
      url: resolvedUrl,
    };
  }

  return null;
}

function maybeParseOverseerCommand(text = '', data = {}) {
  const raw = (text || '').trim();
  if (!raw) return null;
  const lower = raw.toLowerCase().replace(/[.?!]+$/, '').trim();

  // 1. Whole Account Audit
  if (/^(?:audit|audit\s+account|health\s+check|check\s+duplicates|run\s+audit|account\s+audit|library\s+audit)$/i.test(lower)) {
    return { action: 'audit_account' };
  }

  // 2. Account Telemetry & Stats
  if (/^(?:stats|account\s+stats|telemetry|overview|my\s+stats|show\s+stats|stats\s+overview)$/i.test(lower)) {
    return { action: 'get_account_stats' };
  }

  // 3. Auto-organize Root Bookmarks
  if (/^(?:organize\s+root|clean\s+root|auto\s+organize|auto\s+organize\s+root|sort\s+root|categorize\s+root)$/i.test(lower)) {
    return { action: 'auto_organize_root' };
  }

  // 4. Vault Security Audit
  if (/^(?:vault\s+audit|check\s+passwords|password\s+security|audit\s+vault|security\s+audit|password\s+audit)$/i.test(lower)) {
    return { action: 'audit_vault_security' };
  }

  // 5. Vault Export & Backup
  if (/^(?:export\s+json|backup\s+json)$/i.test(lower)) {
    return { action: 'export_vault', format: 'json' };
  }
  if (/^(?:export\s+(?:md|markdown|obsidian)|backup\s+markdown)$/i.test(lower)) {
    return { action: 'export_vault', format: 'markdown' };
  }
  if (/^(?:export|export\s+vault|export\s+html|backup|backup\s+bookmarks|download\s+backup)$/i.test(lower)) {
    return { action: 'export_vault', format: 'html' };
  }

  // 6. Theme Presets
  const themeMatch = lower.match(/^(?:theme|preset|apply\s+theme)\s+(cyberpunk|matrix|quantum|solarized|velvet|emerald|amber|rose|cobalt)$/i);
  if (themeMatch) {
    return { action: 'apply_theme_preset', preset: themeMatch[1] };
  }

  // 7. View Density
  if (/^(?:dense\s+view|compact\s+view|dense\s+grid|set\s+dense)$/i.test(lower)) {
    return { action: 'set_view_density', density: 'dense' };
  }
  if (/^(?:cinema\s+view|large\s+view|cinema\s+grid|set\s+cinema)$/i.test(lower)) {
    return { action: 'set_view_density', density: 'cinema' };
  }
  if (/^(?:standard\s+view|normal\s+view|std\s+grid|set\s+standard)$/i.test(lower)) {
    return { action: 'set_view_density', density: 'standard' };
  }

  // 8. Open Folder in Parallel Tabs
  const openFolderMatch = lower.match(/^(?:open\s+folder|open\s+all\s+in|open\s+all\s+from|launch\s+folder)\s+(.+)$/i);
  if (openFolderMatch) {
    const fName = openFolderMatch[1].replace(/^["']|["']$/g, '').trim();
    if (fName) return { action: 'open_folder_tabs', folderTitle: fName };
  }

  // 9. Clean or Find Duplicates
  if (/^(?:clean\s+duplicates|remove\s+duplicates|delete\s+duplicates|purge\s+duplicates|dedupe|dedupe\s+library)$/i.test(lower)) {
    return { action: 'clean_duplicates' };
  }
  if (/^(?:find\s+duplicates|check\s+duplicates|show\s+duplicates|list\s+duplicates)$/i.test(lower)) {
    return { action: 'find_duplicates' };
  }

  // 10. Purge Empty Folders
  if (/^(?:clean\s+empty\s+folders|purge\s+empty\s+folders|delete\s+empty\s+folders|remove\s+empty\s+folders)$/i.test(lower)) {
    return { action: 'clean_empty_folders' };
  }

  // 11. Reset or Clear View Filters
  if (/^(?:clear\s+filters?|reset\s+filters?|show\s+all)$/i.test(lower)) {
    return { action: 'reset_filter' };
  }

  // 12. Filter View by Tag or Keyword
  const filterTagMatch = lower.match(/^(?:filter|show\s+only)\s+(?:tag\s+)?#?([a-zA-Z0-9_\-]+)$/i);
  if (filterTagMatch) {
    return { action: 'filter_view', tag: filterTagMatch[1] };
  }

  // 13. Batch Tagging across Folders or Domains
  const batchTagDomainMatch = lower.match(/^(?:batch\s+tag|tag\s+all)\s+([a-zA-Z0-9_\-.]+)\s+with\s+(.+)$/i);
  if (batchTagDomainMatch) {
    return { action: 'batch_tag', domain: batchTagDomainMatch[1], tags: batchTagDomainMatch[2] };
  }
  const batchTagFolderMatch = lower.match(/^tag\s+folder\s+(.+?)\s+with\s+(.+)$/i);
  if (batchTagFolderMatch) {
    return { action: 'batch_tag', folderTitle: batchTagFolderMatch[1], tags: batchTagFolderMatch[2] };
  }

  // 14. AI Capabilities & Sovereign Help
  if (/^(?:help|what\s+can\s+you\s+do|capabilities|ai\s+capabilities|show\s+commands|commands|\/help)$/i.test(lower)) {
    return { action: 'get_ai_capabilities' };
  }

  // 15. Check Link Health & Heal Broken Links
  if (/^(?:heal\s+broken\s+links|heal\s+links|repair\s+links|fix\s+broken\s+links|fix\s+links|\/heal)$/i.test(lower)) {
    return { action: 'heal_broken_links' };
  }
  if (/^(?:check\s+link\s+health|check\s+links|link\s+health|test\s+links|scan\s+links)$/i.test(lower)) {
    return { action: 'check_link_health' };
  }

  // 16. Harmonize Tags
  if (/^(?:harmonize\s+tags|clean\s+tags|merge\s+tags|dedupe\s+tags|normalize\s+tags|\/harmonize)$/i.test(lower)) {
    return { action: 'harmonize_tags' };
  }

  // 17. Cluster Bookmarks by Domain
  if (/^(?:cluster\s+by\s+domain|cluster\s+domains|cluster\s+bookmarks|group\s+by\s+domain|group\s+domains|\/cluster)$/i.test(lower)) {
    return { action: 'cluster_by_domain' };
  }

  // 18. Sanitize Tracking Params (UTM, fbclid, etc.)
  if (/^(?:sanitize\s+tracking(?:\s+params)?|clean\s+tracking(?:\s+params)?|remove\s+utm(?:\s+tags)?|strip\s+tracking|clean\s+urls|\/sanitize)$/i.test(lower)) {
    return { action: 'sanitize_tracking_params' };
  }

  // 19. Archive Stale Bookmarks
  if (/^(?:archive\s+stale(?:\s+bookmarks)?|archive\s+inactive|clean\s+stale|stale\s+bookmarks|\/archive)$/i.test(lower)) {
    return { action: 'archive_stale_bookmarks' };
  }

  // 20. Set Sort Mode
  if (/^(?:sort\s+by\s+(?:newest|recent|date\s+desc)|sort\s+newest|\/sort\s+newest)$/i.test(lower)) {
    return { action: 'set_sort_mode', mode: 'date-desc' };
  }
  if (/^(?:sort\s+by\s+(?:oldest|date\s+asc)|sort\s+oldest|\/sort\s+oldest)$/i.test(lower)) {
    return { action: 'set_sort_mode', mode: 'date-asc' };
  }
  if (/^(?:sort\s+by\s+(?:name|title|alpha(?:betical)?|a-z)|sort\s+alpha|\/sort\s+alpha)$/i.test(lower)) {
    return { action: 'set_sort_mode', mode: 'alpha-asc' };
  }
  if (/^(?:sort\s+by\s+(?:z-a|reverse\s+alpha)|sort\s+z-a|\/sort\s+z-a)$/i.test(lower)) {
    return { action: 'set_sort_mode', mode: 'alpha-desc' };
  }
  if (/^(?:sort\s+by\s+(?:visited|most\s+visited|popular)|sort\s+visited|\/sort\s+visited)$/i.test(lower)) {
    return { action: 'set_sort_mode', mode: 'most-visited' };
  }

  // 21. Wayback Machine Archive Snapshot
  const waybackMatch = lower.match(/^(?:wayback|wayback\s+machine|archive\s+snapshot|historical\s+snapshot|\/wayback)(?:\s+(.+))?$/i);
  if (waybackMatch) {
    return { action: 'wayback_restore', query: (waybackMatch[1] || '').trim() };
  }

  // 22. Clone / Duplicate Folder
  const cloneMatch = lower.match(/^(?:clone\s+folder|duplicate\s+folder|copy\s+folder)\s+(.+)$/i);
  if (cloneMatch) {
    return { action: 'clone_folder', folderTitle: cloneMatch[1].replace(/^["']|["']$/g, '').trim() };
  }

  // 23. Summarize Topics & Knowledge Breakdown
  if (/^(?:summarize\s+topics|topic\s+breakdown|library\s+breakdown|vault\s+topics|topic\s+clusters|\/topics)$/i.test(lower)) {
    return { action: 'summarize_vault_topics' };
  }

  // 24. Merge Folders
  const mergeMatch = lower.match(/^(?:merge\s+folder|merge)\s+(.+?)\s+(?:into|to)\s+(.+)$/i);
  if (mergeMatch) {
    return { action: 'merge_folders', sourceFolder: mergeMatch[1].replace(/^["']|["']$/g, '').trim(), targetFolder: mergeMatch[2].replace(/^["']|["']$/g, '').trim() };
  }

  // 25. Export Specific Folder
  const exportFolderMatch = lower.match(/^export\s+folder\s+(.+)$/i);
  if (exportFolderMatch) {
    return { action: 'export_selected_folder', folderTitle: exportFolderMatch[1].replace(/^["']|["']$/g, '').trim() };
  }

  // 26. Curated Daily Reading Queue
  if (/^(?:reading\s+queue|reading\s+list|what\s+should\s+i\s+read|my\s+reading\s+list|\/reading)$/i.test(lower)) {
    return { action: 'ai_reading_queue' };
  }

  // 27. Clean Bookmark Title Suffixes
  if (/^(?:clean\s+titles|strip\s+title\s+suffixes|clean\s+title\s+suffixes|tidy\s+titles|\/titles)$/i.test(lower)) {
    return { action: 'clean_title_suffixes' };
  }

  // 28. Semantic / Smart Library Search
  const semanticSearchMatch = lower.match(/^(?:semantic\s+search|smart\s+search|find\s+concept|\/search)(?:\s+(.+))?$/i);
  if (semanticSearchMatch) {
    return { action: 'semantic_search_library', query: (semanticSearchMatch[1] || '').trim() };
  }

  // 29. Auto Color Folders with Vibrant Palette
  if (/^(?:auto\s+color\s+folders|color\s+folders|palette\s+folders|colorize\s+folders|\/colorfolders)$/i.test(lower)) {
    return { action: 'auto_color_folders' };
  }

  // 30. Tag Estimated Reading Durations
  if (/^(?:tag\s+reading\s+durations|estimate\s+reading\s+times|tag\s+durations|reading\s+times|\/durations)$/i.test(lower)) {
    return { action: 'tag_reading_durations' };
  }

  // 31. Audit Insecure Mixed Content (HTTP)
  if (/^(?:audit\s+mixed\s+content|mixed\s+content|insecure\s+links|find\s+http|\/insecure)$/i.test(lower)) {
    return { action: 'audit_mixed_content' };
  }

  // 32. Compare Library Snapshots (Diff)
  if (/^(?:compare\s+snapshots|library\s+diff|snapshot\s+diff|history\s+diff|\/diff)$/i.test(lower)) {
    return { action: 'compare_library_snapshots' };
  }

  // 33. Bulk Untag (Remove Tag Across Library)
  const bulkUntagMatch = lower.match(/^(?:bulk\s+untag|remove\s+tag|untag|delete\s+tag|\/untag)(?:\s+(.+))?$/i);
  if (bulkUntagMatch && bulkUntagMatch[1]) {
    return { action: 'bulk_untag', tag: bulkUntagMatch[1].replace(/^[#]/, '').trim() };
  }

  // 34. Find Lonely / Orphan Bookmarks
  if (/^(?:lonely\s+bookmarks|find\s+orphans|orphan\s+bookmarks|untagged\s+and\s+unorganized|\/lonely)$/i.test(lower)) {
    return { action: 'find_lonely_bookmarks' };
  }

  // 35. Unfurl & Detect Shortlinks
  if (/^(?:unfurl\s+shortlinks|expand\s+shortlinks|detect\s+shortlinks|short\s+links|\/unfurl)$/i.test(lower)) {
    return { action: 'unfurl_shortlinks' };
  }

  // 36. Daily Executive Morning Briefing
  if (/^(?:daily\s+briefing|morning\s+briefing|executive\s+briefing|reading\s+briefing|\/briefing)$/i.test(lower)) {
    return { action: 'generate_daily_briefing' };
  }

  // 37. Batch Tag by Domain
  if (/^(?:tag\s+domains|auto\s+tag\s+domains|domain\s+tags|\/tagdomains)$/i.test(lower)) {
    return { action: 'batch_tag_by_domain' };
  }

  // 38. Dedupe Bookmark Notes
  if (/^(?:dedupe\s+notes|clean\s+notes|duplicate\s+notes|\/dedupenotes)$/i.test(lower)) {
    return { action: 'dedupe_notes' };
  }

  // 39. Export Markdown Table
  const tableMatch = lower.match(/^(?:markdown\s+table|export\s+table|table\s+export|\/table)(?:\s+(.+))?$/i);
  if (tableMatch) {
    return { action: 'export_markdown_table', folderTitle: (tableMatch[1] || '').trim() };
  }

  // 40. Audit Stale & Weak Credentials
  if (/^(?:stale\s+credentials|weak\s+passwords|check\s+weak\s+passwords|\/stale)$/i.test(lower)) {
    return { action: 'audit_stale_credentials' };
  }

  // 41. Flatten Deep Folder Hierarchy
  if (/^(?:flatten\s+folders|flatten\s+deep\s+folders|flatten\s+hierarchy|\/flatten)$/i.test(lower)) {
    return { action: 'flatten_deep_folders' };
  }

  // 42. Bulk Rename Prefix
  const bulkRenameMatch = lower.match(/^(?:bulk\s+rename|batch\s+rename)\s+(.+)$/i);
  if (bulkRenameMatch) {
    return { action: 'bulk_rename_bookmarks', prefix: bulkRenameMatch[1].trim() };
  }

  // 43. AI Engine Diagnostics
  if (/^(?:ai\s+diagnostics|diagnostics|ai\s+status|engine\s+status|\/diagnostics)$/i.test(lower)) {
    return { action: 'ai_diagnostics' };
  }

  return null;
}

export default function AIAssistant({
  isOpen,
  onClose,
  themeColors: tc,
  data,
  addBookmark,
  addFolder,
  editBookmark,
  editFolder,
  removeBookmark,
  removeFolder,
  moveBookmarkToFolder,
  moveFolderToFolder,
  currentFolderId,
  onShareFolder,
  settings,
  onSettingsChange,
  isDark: isDarkProp,
  onOpenSettings,
}) {
  const [messages, setMessages] = useState([WELCOME_MSG]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [activeVaultCredentialItem, setActiveVaultCredentialItem] = useState(null);
  const [isListening, setIsListening] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  const toggleVoiceInput = useCallback(() => {
    const SpeechRecognition = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SpeechRecognition) {
      alert('Speech recognition is not supported in this browser.');
      return;
    }

    if (isListening) {
      try { recognitionRef.current?.stop(); } catch (_) {}
      setIsListening(false);
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => setIsListening(true);
      recognition.onend = () => setIsListening(false);
      recognition.onerror = () => setIsListening(false);

      recognition.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map((r) => r[0].transcript)
          .join('');
        setInput(transcript);
        if (event.results[0] && event.results[0].isFinal) {
          setIsListening(false);
          setTimeout(() => {
            sendMessage(transcript);
          }, 250);
        }
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (e) {
      console.warn('Speech recognition error:', e);
      setIsListening(false);
    }
  }, [isListening]);

  const SLASH_COMMANDS = useMemo(
    () => [
      { cmd: '/audit', desc: 'Account health & telemetry audit', exec: 'audit account' },
      { cmd: '/organize', desc: 'Auto-organize loose root bookmarks', exec: 'organize root' },
      { cmd: '/duplicates', desc: 'Clean duplicate bookmark URLs', exec: 'clean duplicates' },
      { cmd: '/clean', desc: 'Purge empty abandoned folders', exec: 'clean empty folders' },
      { cmd: '/export', desc: 'Download offline HTML vault backup', exec: 'export vault' },
      { cmd: '/vault', desc: 'Audit passwords and vault security', exec: 'audit vault security' },
      { cmd: '/stats', desc: 'View account statistics & telemetry', exec: 'stats' },
      { cmd: '/theme cyberpunk', desc: 'Switch theme (matrix, emerald...)', exec: 'theme cyberpunk' },
      { cmd: '/density dense', desc: 'Set layout density (dense, cinema...)', exec: 'dense view' },
      { cmd: '/heal', desc: 'Auto-upgrade http->https and heal URLs', exec: 'heal broken links' },
      { cmd: '/harmonize', desc: 'Normalize casing & merge synonym tags', exec: 'harmonize tags' },
      { cmd: '/cluster', desc: 'Group domain bookmarks into tidy folders', exec: 'cluster by domain' },
      { cmd: '/sanitize', desc: 'Scrub UTM & tracking params from URLs', exec: 'sanitize tracking params' },
      { cmd: '/archive', desc: 'Move stale & unvisited links to Archive', exec: 'archive stale bookmarks' },
      { cmd: '/sort newest', desc: 'Sort bookmarks by newest/recent date', exec: 'sort by newest' },
      { cmd: '/wayback', desc: 'Find Wayback Machine archive for URL', exec: 'wayback' },
      { cmd: '/topics', desc: 'Executive topic & knowledge breakdown', exec: 'summarize topics' },
      { cmd: '/reading', desc: 'Curated daily 3-article reading queue', exec: 'reading queue' },
      { cmd: '/titles', desc: 'Clean repetitive website title suffixes', exec: 'clean titles' },
      { cmd: '/search', desc: 'Semantic / conceptual library search', exec: 'semantic search ' },
      { cmd: '/colorfolders', desc: 'Auto-assign vibrant colors to folders', exec: 'auto color folders' },
      { cmd: '/durations', desc: 'Estimate & tag reading times (1-min, 5-min)', exec: 'tag reading durations' },
      { cmd: '/insecure', desc: 'Audit unencrypted http:// mixed content', exec: 'audit mixed content' },
      { cmd: '/diff', desc: 'Diff library snapshot against past save', exec: 'compare snapshots' },
      { cmd: '/untag', desc: 'Bulk remove a tag across entire library', exec: 'bulk untag ' },
      { cmd: '/lonely', desc: 'Find untagged, root-level lonely links', exec: 'lonely bookmarks' },
      { cmd: '/unfurl', desc: 'Detect bit.ly/t.co URL shorteners', exec: 'unfurl shortlinks' },
      { cmd: '/briefing', desc: 'Daily executive AI reading digest', exec: 'daily briefing' },
      { cmd: '/tagdomains', desc: 'Auto-apply tags based on website domains', exec: 'tag domains' },
      { cmd: '/dedupenotes', desc: 'Detect redundant copy-pasted notes', exec: 'dedupe notes' },
      { cmd: '/table', desc: 'Export bookmarks as GitHub Markdown table', exec: 'markdown table' },
      { cmd: '/stale', desc: 'Audit weak/stale vault credentials', exec: 'stale credentials' },
      { cmd: '/flatten', desc: 'Flatten deeply nested folder tiers', exec: 'flatten folders' },
      { cmd: '/diagnostics', desc: 'View AI engine telemetry & stats', exec: 'ai diagnostics' },
      { cmd: '/help', desc: 'Show interactive AI capability matrix', exec: 'what can you do' },
    ],
    []
  );

  const filteredSlashCommands = useMemo(() => {
    if (!input.startsWith('/')) return [];
    const prefix = input.toLowerCase();
    return SLASH_COMMANDS.filter((c) => c.cmd.toLowerCase().startsWith(prefix));
  }, [input, SLASH_COMMANDS]);

  const dynamicChips = useMemo(() => {
    const all = Object.values(data || {}).flatMap((f) => (f.items || []).filter((i) => i.type === 'link'));
    const rootItems = (data?.root?.items || []).filter((i) => i.type === 'link');
    const allFolders = Object.values(data || {}).filter((f) => f.id !== 'root');
    const emptyCount = allFolders.filter((f) => {
      const hasBm = (f.items || []).filter((i) => i.type === 'link').length > 0;
      return !hasBm;
    }).length;

    const urlMap = new Set();
    let dupCount = 0;
    for (const b of all) {
      if (!b.url) continue;
      const key = b.url.toLowerCase().replace(/\/+$/, '');
      if (urlMap.has(key)) dupCount++;
      else urlMap.add(key);
    }

    const list = [];
    if (dupCount > 0) {
      list.push({ label: `🧹 Clean ${dupCount} Duplicates`, text: 'clean duplicates' });
    }
    if (rootItems.length > 3) {
      list.push({ label: `📁 Organize ${rootItems.length} Root Links`, text: 'organize root' });
    }
    if (emptyCount > 0) {
      list.push({ label: `🗑️ Purge ${emptyCount} Empty Folders`, text: 'clean empty folders' });
    }
    list.push({ label: '📊 Health Audit', text: 'audit account' });
    list.push({ label: '🔒 Vault Security', text: 'audit vault security' });
    list.push({ label: '💾 Offline Vault', text: 'export vault' });
    list.push({ label: '✨ Capabilities', text: 'what can you do' });
    return list;
  }, [data]);

  const isDark = isDarkProp !== undefined ? Boolean(isDarkProp) : settings?.isDark !== false;

  const dataRef = useRef(data);
  const settingsRef = useRef(settings);
  const folderRegistryRef = useRef({});

  const requestSeqRef = useRef(0);
  const latestHandledRef = useRef(0);
  const executedActionHashesRef = useRef(new Set());
  const turnsCountRef = useRef(0);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
        inputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  const executeAction = useCallback(
    makeExecuteAction({
      dataRef,
      settingsRef,
      folderRegistryRef,
      addBookmark,
      addFolder,
      editBookmark,
      editFolder,
      removeBookmark,
      removeFolder,
      moveBookmarkToFolder,
      moveFolderToFolder,
      onShareFolder,
      onSettingsChange,
    }),
    [
      addBookmark,
      addFolder,
      editBookmark,
      editFolder,
      removeBookmark,
      removeFolder,
      moveBookmarkToFolder,
      moveFolderToFolder,
      onShareFolder,
      onSettingsChange,
    ]
  );

  const requestModel = useCallback(async ({ userText, systemPrompt, history }) => {
    let lastReply = '';
    let lastErr = null;

    // Iterate through free models pool with auto-fallback
    for (const modelToUse of FREE_MODELS_POOL) {
      for (let attempt = 1; attempt <= 2; attempt++) {
        try {
          const bodyPayload = {
            model: modelToUse,
            messages: [
              { role: 'system', content: systemPrompt },
              ...history,
              {
                role: 'system',
                content: `Execute ONLY the latest user request in this turn: "${userText}". Emit any tool actions as standard JSON array [{"action":"..."}]. Do NOT output raw tokens like <|tool_call_start|> or Python syntax. Do NOT repeat prior completed actions unless explicitly requested now.`,
              },
              { role: 'user', content: userText },
            ],
            temperature: 0.15,
            top_p: 0.3,
            repetition_penalty: 1.05,
            max_tokens: isActionIntent(userText) ? 2200 : 700,
          };

          const userByokKey = localStorage.getItem('memark_byok_api_key') || '';
          if (!userByokKey) {
            throw new Error('Please input your personal OpenRouter API key in Settings -> AI Gateway to use the AI Copilot.');
          }

          // 1. Direct OpenRouter call with user's personal BYOK key
          let res = null;
          try {
            res = await fetch('https://openrouter.ai/api/v1/chat/completions', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${userByokKey}`,
                'HTTP-Referer': window.location.origin,
                'X-Title': 'MeMark BYOK AI',
              },
              body: JSON.stringify(bodyPayload),
            });
          } catch (_) {
            res = null;
          }

          // 2. Try reverse proxy with personal BYOK token
          if (!res || !res.ok) {
            try {
              res = await fetch('/api/ai/chat', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${userByokKey}`,
                  'HTTP-Referer': window.location.origin,
                  'X-Title': 'MeMark AI Dashboard',
                },
                body: JSON.stringify(bodyPayload),
              });
              if (res.status === 404) res = null;
            } catch (_) {
              res = null;
            }
          }

          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw new Error(`HTTP ${res.status}: ${errData?.error?.message || res.statusText}`);
          }

          const json = await res.json();
          let reply = json?.choices?.[0]?.message?.content || '';
          // Clean thinking blocks
          reply = reply.replace(/<think>[\s\S]*?<\/think>/gi, '').trim();
          lastReply = reply;

          if (!reply || !reply.trim()) throw new Error('Empty model response');

          if (!isActionIntent(userText)) return reply;

          const parsed = parseActions(reply);
          if (parsed.length > 0) return reply;

          const unknown = extractUnknownActions(reply);
          if (unknown.length > 0) return reply;

          // If the model produced helpful conversational text, return it directly
          if (reply.trim()) return reply;

          throw new Error('No valid action JSON in reply');
        } catch (err) {
          lastErr = err;
          console.warn(`[AI] Attempt failed with model ${modelToUse}:`, err.message);
          if (attempt < 2) await wait(300 * attempt);
        }
      }
    }

    if (lastReply && lastReply.trim()) return lastReply;
    throw lastErr || new Error('All free AI models are currently busy. Please retry in a moment.');
  }, []);

  const sendMessage = useCallback(
    async (text) => {
      const userText = (text || input).trim();
      if (!userText || loading) return;

      const userByokKey = localStorage.getItem('memark_byok_api_key') || '';
      if (!userByokKey) {
        const userMsg = { role: 'user', content: userText, id: Date.now() };
        setInput('');
        setMessages((prev) => [
          ...prev,
          userMsg,
          {
            role: 'assistant',
            content:
              '🔑 **OpenRouter API Key Required**\n\n' +
              'To use the AI Assistant, please input your personal OpenRouter API key in **Settings → AI Gateway**.\n\n' +
              'Your key is saved **locally on your device** (`localStorage`) and is never shared or stored in any cloud database.',
            id: Date.now() + 1,
            requireKey: true,
          },
        ]);
        return;
      }

      const userMsg = { role: 'user', content: userText, id: Date.now() };

      const shortcutActions = maybeParseBookmarkShortcut(userText);
      if (shortcutActions) {
        const requestId = ++requestSeqRef.current;
        turnsCountRef.current += 1;

        if (turnsCountRef.current % 50 === 0) {
          executedActionHashesRef.current = new Set();
        }

        setInput('');
        folderRegistryRef.current = {};
        setMessages((prev) => [...prev, userMsg]);
        setLoading(true);

        try {
          for (const rawAction of shortcutActions) {
            const action = normalizeAction(rawAction);
            const hash = `${action.action}::${stableStringify(action)}`;

            if (executedActionHashesRef.current.has(hash)) continue;
            executedActionHashesRef.current.add(hash);

            await executeAction(action);

            setMessages((prev) => [
              ...prev,
              {
                role: 'action',
                action,
                id: `act_${Date.now()}_${Math.random()}`,
              },
            ]);

            if (action.result) {
              setMessages((prev) => [
                ...prev,
                {
                  role: 'assistant',
                  content: action.result,
                  id: `result_${Date.now()}_${Math.random()}`,
                },
              ]);
            }
          }

          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: 'Saved that bookmark.',
              id: `done_${Date.now()}`,
            },
          ]);

          latestHandledRef.current = requestId;
        } catch (err) {
          console.error('Bookmark shortcut failed:', err);
          setMessages((prev) => [
            ...prev,
            {
              role: 'system',
              content: `⚠️ Failed to save bookmark — ${err.message || 'Unknown error'}`,
              id: `err_${Date.now()}`,
            },
          ]);
        } finally {
          if (requestId === requestSeqRef.current) setLoading(false);
        }

        return;
      }

      // Deterministic zero-latency client fast-path for opening bookmarks or external web links
      const directOpenAction = maybeParseDirectOpen(userText, dataRef.current);
      if (directOpenAction) {
        const requestId = ++requestSeqRef.current;
        turnsCountRef.current += 1;

        if (turnsCountRef.current % 50 === 0) {
          executedActionHashesRef.current = new Set();
        }

        setInput('');
        folderRegistryRef.current = {};
        setMessages((prev) => [...prev, userMsg]);
        setLoading(true);

        try {
          await executeAction(directOpenAction);

          setMessages((prev) => [
            ...prev,
            {
              role: 'action',
              action: directOpenAction,
              id: `act_${Date.now()}_${Math.random()}`,
            },
          ]);

          if (directOpenAction.result) {
            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                content: directOpenAction.result,
                id: `result_${Date.now()}_${Math.random()}`,
              },
            ]);
          }

          latestHandledRef.current = requestId;
        } catch (err) {
          console.error('Direct open failed:', err);
          setMessages((prev) => [
            ...prev,
            {
              role: 'system',
              content: `⚠️ Failed to open — ${err.message || 'Unknown error'}`,
              id: `err_${Date.now()}`,
            },
          ]);
        } finally {
          if (requestId === requestSeqRef.current) setLoading(false);
        }

        return;
      }

      // Deterministic zero-latency client fast-path for Whole-Account Overseer commands
      const overseerAction = maybeParseOverseerCommand(userText, dataRef.current);
      if (overseerAction) {
        const requestId = ++requestSeqRef.current;
        turnsCountRef.current += 1;

        if (turnsCountRef.current % 50 === 0) {
          executedActionHashesRef.current = new Set();
        }

        setInput('');
        folderRegistryRef.current = {};
        setMessages((prev) => [...prev, userMsg]);
        setLoading(true);

        try {
          await executeAction(overseerAction);

          setMessages((prev) => [
            ...prev,
            {
              role: 'action',
              action: overseerAction,
              id: `act_${Date.now()}_${Math.random()}`,
            },
          ]);

          if (overseerAction.result) {
            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                content: overseerAction.result,
                id: `result_${Date.now()}_${Math.random()}`,
              },
            ]);
          }

          latestHandledRef.current = requestId;
        } catch (err) {
          console.error('Overseer action failed:', err);
          setMessages((prev) => [
            ...prev,
            {
              role: 'system',
              content: `⚠️ "${overseerAction.action}" failed — ${err.message || 'Unknown error'}`,
              id: `err_${Date.now()}`,
            },
          ]);
        } finally {
          if (requestId === requestSeqRef.current) setLoading(false);
        }

        return;
      }

      if (pendingConfirmation) {
        if (isConfirmCommand(userText)) {
          setMessages((prev) => [
            ...prev,
            userMsg,
            { role: 'assistant', content: 'Confirmed. Executing now...', id: `confirm_${Date.now()}` },
          ]);
          setInput('');
          setLoading(true);

          const queuedActions = pendingConfirmation.actions || [];
          setPendingConfirmation(null);

          try {
            for (const action of queuedActions) {
              try {
                await executeAction(action);

                setMessages((prev) => [
                  ...prev,
                  {
                    role: 'action',
                    action,
                    id: `act_${Date.now()}_${Math.random()}`,
                  },
                ]);

                if (action.result) {
                  setMessages((prev) => [
                    ...prev,
                    {
                      role: 'assistant',
                      content: action.result,
                      id: `result_${Date.now()}_${Math.random()}`,
                    },
                  ]);
                }
              } catch (err) {
                console.error('Confirmed action failed:', action, err);
                setMessages((prev) => [
                  ...prev,
                  {
                    role: 'system',
                    content: `⚠️ "${action.action}" failed — ${err.message || 'Unknown error'}`,
                    id: `err_${Date.now()}`,
                  },
                ]);
              }
            }

            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                content: 'Done.',
                id: `done_${Date.now()}`,
              },
            ]);
          } finally {
            setLoading(false);
          }
          return;
        }

        if (isCancelCommand(userText)) {
          setMessages((prev) => [
            ...prev,
            userMsg,
            {
              role: 'assistant',
              content: 'Cancelled. No destructive actions were executed.',
              id: `cancel_${Date.now()}`,
            },
          ]);
          setInput('');
          setPendingConfirmation(null);
          return;
        }

        setMessages((prev) => [
          ...prev,
          userMsg,
          {
            role: 'assistant',
            content: 'A destructive action is waiting for confirmation. Reply **confirm** to continue or **cancel** to abort.',
            id: `pending_${Date.now()}`,
          },
        ]);
        setInput('');
        return;
      }

      const requestId = ++requestSeqRef.current;
      turnsCountRef.current += 1;

      if (turnsCountRef.current % 50 === 0) {
        executedActionHashesRef.current = new Set();
      }

      setInput('');
      folderRegistryRef.current = {};

      const typingMsg = { role: 'assistant', content: '', typing: true, id: `typing_${requestId}` };
      setMessages((prev) => [...prev, userMsg, typingMsg]);
      setLoading(true);

      const maybeColor = toHexIfNamed(userText);
      const prevUser = lastUserText(messages);
      const prevLower = (prevUser || '').toLowerCase();
      const looksLikeBgFollowup =
        !!maybeColor &&
        (
          /what color|which color|turn it back|back to/.test(prevLower) ||
          /background|page bg|page background/.test(prevLower)
        );

      if (looksLikeBgFollowup) {
        try {
          const synthetic = { action: 'apply_font_colors', pageBg: maybeColor };
          await executeAction(synthetic);

          setMessages((prev) => prev.filter((m) => m.id !== `typing_${requestId}`));
          setMessages((prev) => [
            ...prev,
            { role: 'action', action: synthetic, id: `act_${Date.now()}_${Math.random()}` },
          ]);
          latestHandledRef.current = requestId;
          return;
        } catch {
        } finally {
          if (requestId === requestSeqRef.current) setLoading(false);
        }
      }

      // Quick re-open / retry intent (e.g. "try open again", "open again", "reopen", "retry open")
      const isRetryOpen = /^(?:try\s+)?(?:open\s+(?:it\s+)?again|reopen|retry\s+open|launch\s+again|open\s+again|try\s+again)$/i.test(userText.trim());
      if (isRetryOpen) {
        const lastOpenMsg = [...messages].reverse().find((m) => m.role === 'action' && m.action?.action === 'open_bookmark');
        const lastOpenAction = lastOpenMsg?.action ? { ...lastOpenMsg.action } : null;
        if (lastOpenAction) {
          try {
            await executeAction(lastOpenAction);
            setMessages((prev) => prev.filter((m) => m.id !== `typing_${requestId}`));
            setMessages((prev) => [
              ...prev,
              { role: 'action', action: lastOpenAction, id: `act_${Date.now()}_${Math.random()}` },
            ]);
            if (lastOpenAction.result) {
              setMessages((prev) => [
                ...prev,
                { role: 'assistant', content: lastOpenAction.result, id: `result_${Date.now()}_${Math.random()}` },
              ]);
            }
            latestHandledRef.current = requestId;
            return;
          } catch (err) {
            console.error('Retry open failed:', err);
          } finally {
            if (requestId === requestSeqRef.current) setLoading(false);
          }
        }
      }

      const historyClean = [];
      const validMsgs = messages.filter(
        (m) =>
          (m.role === 'user' || m.role === 'assistant' || m.role === 'action' || m.role === 'system') &&
          !m.typing &&
          m.id !== 'init'
      );

      let i = 0;
      while (i < validMsgs.length) {
        const m = validMsgs[i];
        if (m.role === 'user') {
          historyClean.push({ role: 'user', content: m.content });
          i++;

          const textParts = [];
          const doneParts = [];

          while (i < validMsgs.length && validMsgs[i].role !== 'user') {
            const r = validMsgs[i];
            if (r.role === 'action') {
              doneParts.push(actionToHistoryText(r.action));
            } else if (r.role === 'assistant' && r.content?.trim()) {
              textParts.push(r.content.trim());
            } else if (r.role === 'system' && r.content?.trim()) {
              textParts.push(`⚠️ System Note: ${r.content.trim()}`);
            }
            i++;
          }

          if (textParts.length > 0) {
            historyClean.push({ role: 'assistant', content: textParts.join('\n\n') });
          }

          if (doneParts.length > 0) {
            historyClean.push({
              role: 'system',
              content: `Completed actions from older turns (do NOT repeat unless user asks again):\n${doneParts.join('\n')}`,
            });
          }
        } else {
          i++;
        }
      }

      const history = historyClean.slice(-3);
      const needsFull = isActionIntent(userText);
      const systemPrompt = needsFull
        ? buildFullPrompt(dataRef.current, settingsRef.current)
        : buildSlimPrompt(dataRef.current, settingsRef.current);

      try {
        const reply = await requestModel({ userText, systemPrompt, history });

        let actions = parseActions(reply);
        const unknownActions = extractUnknownActions(reply);
        const displayText = cleanAssistantDisplayText(reply);

        if (requestId !== requestSeqRef.current) return;
        if (requestId <= latestHandledRef.current) return;

        setMessages((prev) => prev.filter((m) => m.id !== `typing_${requestId}`));

        actions = actions.map(normalizeAction);
        actions = dedupeActions(actions);

        const isRetryOrRepeat = /\b(again|retry|reopen|redo|re-open|once\s+more|try\s+again|one\s+more\s+time)\b/i.test(userText);

        actions = actions.filter((a) => {
          // Never filter out read-only, navigation, or retrieval actions
          if (
            a.action === 'open_bookmark' ||
            a.action === 'get_credentials' ||
            a.action === 'get_note' ||
            a.action === 'list_bookmarks' ||
            a.action === 'audit_account' ||
            a.action === 'get_account_stats' ||
            a.action === 'audit_vault_security' ||
            a.action === 'export_vault' ||
            a.action === 'open_folder_tabs' ||
            a.action === 'search_library' ||
            a.action === 'find_duplicates' ||
            a.action === 'clean_duplicates' ||
            a.action === 'clean_empty_folders' ||
            a.action === 'purge_empty_folders' ||
            a.action === 'reset_filter' ||
            a.action === 'filter_view' ||
            a.action === 'batch_tag' ||
            a.action === 'get_ai_capabilities' ||
            a.action === 'check_link_health' ||
            a.action === 'heal_broken_links' ||
            a.action === 'harmonize_tags' ||
            a.action === 'cluster_by_domain' ||
            a.action === 'sanitize_tracking_params' ||
            a.action === 'archive_stale_bookmarks' ||
            a.action === 'set_sort_mode' ||
            a.action === 'wayback_restore' ||
            a.action === 'archive_snapshot' ||
            a.action === 'clone_folder' ||
            a.action === 'duplicate_folder' ||
            a.action === 'summarize_vault_topics' ||
            a.action === 'merge_folders' ||
            a.action === 'export_selected_folder' ||
            a.action === 'ai_reading_queue' ||
            a.action === 'clean_title_suffixes' ||
            a.action === 'semantic_search_library' ||
            a.action === 'auto_color_folders' ||
            a.action === 'tag_reading_durations' ||
            a.action === 'audit_mixed_content' ||
            a.action === 'compare_library_snapshots' ||
            a.action === 'bulk_untag' ||
            a.action === 'find_lonely_bookmarks' ||
            a.action === 'unfurl_shortlinks' ||
            a.action === 'generate_daily_briefing' ||
            a.action === 'batch_tag_by_domain' ||
            a.action === 'dedupe_notes' ||
            a.action === 'export_markdown_table' ||
            a.action === 'audit_stale_credentials' ||
            a.action === 'flatten_deep_folders' ||
            a.action === 'bulk_rename_bookmarks' ||
            a.action === 'ai_diagnostics'
          ) {
            return true;
          }

          // If the user explicitly asks to retry or repeat, allow execution
          if (isRetryOrRepeat) {
            return true;
          }

          const hash = `${a.action}::${stableStringify(a)}`;
          if (executedActionHashesRef.current.has(hash)) return false;
          return true;
        });

        if (displayText) {
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', content: displayText, id: Date.now() },
          ]);
        } else if (actions.length === 0 && unknownActions.length > 0) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'system',
              content: `⚠️ The AI returned unsupported action(s): ${unknownActions.map((a) => a.action).join(', ')}`,
              id: `err_${Date.now()}`,
            },
            {
              role: 'assistant',
              content: 'The AI produced an unsupported action, so nothing was executed. Please try rephrasing your request.',
              id: `assist_${Date.now()}_${Math.random()}`,
            },
          ]);
        } else if (actions.length === 0 && !displayText) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: 'I processed your request! Let me know if you would like me to add, move, or organize any bookmarks.',
              id: Date.now(),
            },
          ]);
        }

        if (actions.length > 0) {
          const hasDestructive = actions.some(isDestructiveAction);

          if (hasDestructive) {
            setPendingConfirmation({
              actions,
              createdAt: Date.now(),
            });

            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                content: buildConfirmationMessage(actions),
                id: `confirm_prompt_${Date.now()}`,
              },
            ]);

            latestHandledRef.current = requestId;
            return;
          }
        }

        for (const action of actions) {
          const hash = `${action.action}::${stableStringify(action)}`;
          executedActionHashesRef.current.add(hash);

          try {
            await executeAction(action);

            if (action.action === 'get_credentials') {
              const credItem = action.bookmark || {
                id: action.id,
                title: action.title,
                url: action.url,
                credentials: action.credentials,
              };
              if (credItem.credentials?.trim()) {
                setActiveVaultCredentialItem(credItem);
              }
            }

            setMessages((prev) => [
              ...prev,
              {
                role: 'action',
                action,
                id: `act_${Date.now()}_${Math.random()}`,
              },
            ]);

            if (action.result) {
              setMessages((prev) => [
                ...prev,
                {
                  role: 'assistant',
                  content: action.result,
                  id: `result_${Date.now()}_${Math.random()}`,
                },
              ]);
            }
          } catch (err) {
            console.error('Action failed:', action, err);
            setMessages((prev) => [
              ...prev,
              {
                role: 'system',
                content: `⚠️ "${action.action}" failed — ${err.message || 'Unknown error'}`,
                id: `err_${Date.now()}`,
              },
            ]);
          }
        }

        latestHandledRef.current = requestId;
      } catch (err) {
        console.error('AI request failed:', err);

        setMessages((prev) => prev.filter((m) => m.id !== `typing_${requestId}`));

        if (requestId === requestSeqRef.current) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: 'AI service issue or network issue. Please retry in a moment.',
              id: Date.now(),
            },
          ]);
        }
      } finally {
        if (requestId === requestSeqRef.current) setLoading(false);
      }
    },
    [input, loading, messages, executeAction, requestModel, pendingConfirmation]
  );

  const clearChat = () => {
    setMessages([WELCOME_MSG]);
    setPendingConfirmation(null);
    executedActionHashesRef.current = new Set();
    latestHandledRef.current = 0;
    requestSeqRef.current = 0;
    turnsCountRef.current = 0;
  };

  const panelBg = isDark ? '#0A0A0AF7' : '#FCFCFCF7';
  const aiBg = tc.aiButtonBg || tc.accent;
  const aiText = tc.aiButtonText || '#FFFFFF';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[60]"
          />

          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.93 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.93 }}
            transition={{ type: 'spring', stiffness: 420, damping: 32 }}
            className="fixed bottom-24 left-6 z-[70] flex flex-col rounded-3xl shadow-2xl overflow-hidden"
            style={{
              width: 360,
              height: 560,
              background: panelBg,
              border: `1px solid ${tc.border}`,
              backdropFilter: 'blur(28px)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0"
              style={{ borderColor: tc.border }}
            >
              <div className="flex items-center gap-2.5">
                <div
                  className="w-8 h-8 rounded-xl flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, ${aiBg}, ${aiBg}88)` }}
                >
                  <SafeIcon icon={FiZap} className="w-4 h-4" style={{ color: aiText }} />
                </div>
                <div>
                  <p className="text-sm font-semibold leading-tight" style={{ color: tc.text }}>
                    AI Assistant
                  </p>
                  <div className="flex items-center gap-1">
                    <p className="text-[10px]" style={{ color: tc.muted }}>
                      Your bookmark manager
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={clearChat}
                  className="p-1.5 rounded-lg transition-colors"
                  style={{ color: tc.muted }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = tc.text)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = tc.muted)}
                  title="Clear chat"
                >
                  <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                </button>

                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg transition-colors"
                  style={{ color: tc.muted }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = tc.text)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = tc.muted)}
                >
                  <SafeIcon icon={FiChevronDown} className="w-4 h-4" />
                </button>
              </div>
            </div>

            <StatsBar data={data} tc={tc} />

            {!localStorage.getItem('memark_byok_api_key') && (
              <button
                type="button"
                onClick={() => { onClose(); onOpenSettings?.('ai'); }}
                className="mx-3 mt-2 mb-1 p-2 rounded-xl text-xs flex items-center justify-between border transition-all hover:scale-[1.01] cursor-pointer"
                style={{
                  backgroundColor: isDark ? 'rgba(234, 179, 8, 0.1)' : 'rgba(234, 179, 8, 0.15)',
                  borderColor: isDark ? 'rgba(234, 179, 8, 0.3)' : 'rgba(234, 179, 8, 0.4)',
                  color: isDark ? '#facc15' : '#b45309',
                }}
              >
                <div className="flex items-center gap-1.5 truncate">
                  <SafeIcon icon={FiKey} className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate font-medium">Personal Key Required</span>
                </div>
                <span className="text-[10px] font-semibold underline shrink-0">Open Settings →</span>
              </button>
            )}

            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1" style={{ scrollbarWidth: 'none' }}>
              {messages.map((msg) =>
                msg.role === 'action'
                  ? <ActionBadge key={msg.id} action={msg.action} tc={tc} data={data} onOpenCredential={(item) => setActiveVaultCredentialItem(item)} />
                  : (
                    <div key={msg.id}>
                      <Message msg={msg} tc={tc} />
                      {msg.requireKey && (
                        <div className="flex justify-start pl-8 -mt-1 mb-3">
                          <button
                            type="button"
                            onClick={() => { onClose(); onOpenSettings?.('ai'); }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-emerald-500 hover:bg-emerald-400 text-black transition-all shadow-sm cursor-pointer"
                          >
                            <SafeIcon icon={FiKey} className="w-3.5 h-3.5" />
                            <span>Configure Key in Settings →</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Interactive Dynamic Prompt Quick-Chips */}
            <div
              className="px-3 pt-1 pb-1 flex items-center gap-1.5 overflow-x-auto scrollbar-none no-scrollbar flex-shrink-0"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              data-no-scrollbar="true"
            >
              {dynamicChips.map((chip) => (
                <button
                  key={chip.label}
                  onClick={() => sendMessage(chip.text)}
                  disabled={loading}
                  className="text-[10px] whitespace-nowrap px-2.5 py-1 rounded-lg border font-medium transition-all hover:scale-105 active:scale-95 disabled:opacity-50 shrink-0 shadow-sm"
                  style={{
                    backgroundColor: `${tc.accent}15`,
                    borderColor: `${tc.accent}35`,
                    color: tc.accent,
                  }}
                >
                  {chip.label}
                </button>
              ))}
            </div>

            <div className="px-3 pb-3 pt-1 flex-shrink-0 relative">
              {/* Slash Command HUD Popover */}
              {filteredSlashCommands.length > 0 && (
                <div
                  className="absolute bottom-full left-3 right-3 mb-2 rounded-2xl border shadow-2xl p-1.5 overflow-hidden z-20 backdrop-blur-xl animate-in fade-in slide-in-from-bottom-2 duration-150"
                  style={{
                    backgroundColor: isDark ? '#141414F5' : '#FFFFFFFA',
                    borderColor: `${tc.accent}40`,
                    maxHeight: 180,
                    overflowY: 'auto',
                  }}
                >
                  <div className="px-2 py-1 text-[10px] font-semibold tracking-wider uppercase opacity-60 flex justify-between">
                    <span>⚡ Quick Slash Commands</span>
                    <span>Click or Enter</span>
                  </div>
                  {filteredSlashCommands.map((sc) => (
                    <button
                      key={sc.cmd}
                      type="button"
                      onClick={() => {
                        setInput('');
                        sendMessage(sc.exec);
                      }}
                      className="w-full text-left px-2.5 py-1.5 rounded-xl text-xs flex items-center justify-between transition-colors hover:bg-white/10 group"
                      style={{ color: tc.text }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold" style={{ color: tc.accent }}>
                          {sc.cmd}
                        </span>
                        <span className="text-[11px] opacity-75">{sc.desc}</span>
                      </div>
                      <span className="text-[10px] opacity-40 group-hover:opacity-100 transition-opacity">
                        ↵ Run
                      </span>
                    </button>
                  ))}
                </div>
              )}

              <div
                className="flex items-center gap-2 px-3 py-2 rounded-2xl border"
                style={{
                  backgroundColor: isDark ? '#FFFFFF0A' : '#0000000A',
                  borderColor: isListening ? '#EF4444' : isDark ? tc.border : '#CBD5E1',
                  boxShadow: isListening ? '0 0 12px rgba(239, 68, 68, 0.4)' : 'none',
                  transition: 'all 0.3s',
                }}
              >
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (filteredSlashCommands.length > 0) {
                        const top = filteredSlashCommands[0];
                        setInput('');
                        sendMessage(top.exec);
                      } else {
                        sendMessage();
                      }
                    }
                  }}
                  placeholder={
                    isListening
                      ? '🎙️ Listening... speak your command'
                      : pendingConfirmation
                      ? 'Type confirm or cancel…'
                      : 'Ask anything or type / for commands…'
                  }
                  className="flex-1 bg-transparent text-sm focus:outline-none"
                  style={{ color: tc.text }}
                  disabled={loading}
                />

                {/* Microphone Voice Dictation Button */}
                <button
                  type="button"
                  onClick={toggleVoiceInput}
                  disabled={loading}
                  className={`w-7 h-7 rounded-xl flex items-center justify-center transition-all flex-shrink-0 ${
                    isListening ? 'animate-pulse bg-red-500/20 text-red-500' : 'text-stone-400 hover:text-stone-200'
                  }`}
                  style={{
                    border: isListening ? '1px solid rgba(239, 68, 68, 0.5)' : 'none',
                  }}
                  title={isListening ? 'Stop listening' : 'Start voice dictation'}
                >
                  <SafeIcon
                    icon={isListening ? FiMicOff : FiMic}
                    className="w-3.5 h-3.5"
                    style={{ color: isListening ? '#EF4444' : tc.muted }}
                  />
                </button>

                {/* Send Button */}
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || loading}
                  className="w-7 h-7 rounded-xl flex items-center justify-center transition-all flex-shrink-0"
                  style={{
                    backgroundColor: input.trim() && !loading ? (tc.chatUserBg || tc.accent) : tc.border,
                    color: input.trim() && !loading ? (tc.chatUser || '#000000') : tc.muted,
                  }}
                >
                  <SafeIcon
                    icon={loading ? FiLoader : FiSend}
                    className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`}
                  />
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}

      {/* Sovereign Secure Credential Popup Enclave */}
      <SecureCredentialPopup
        isOpen={Boolean(activeVaultCredentialItem)}
        onClose={() => setActiveVaultCredentialItem(null)}
        item={activeVaultCredentialItem}
        themeColors={tc}
        isDark={isDark}
      />
    </AnimatePresence>
  );
}